import { useCallback, useEffect, useState } from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBSpinner,
  MDBListGroup,
  MDBListGroupItem,
} from "mdb-react-ui-kit";
import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

/**
 * ExhibicionShareModal
 *
 * Props:
 *   show         {boolean}
 *   setShow      {Function(bool)}
 *   idExhibicion {number}
 *   appCodes     {string[]}  — apps a las que pertenece la exhibición (para obtener usuarios)
 *   onChanged    {Function}  — callback cuando cambia algo (para recargar lista padre)
 */
export default function ExhibicionShareModal({ show, setShow, idExhibicion, appCodes = [], onChanged }) {
  const handleClose = useCallback(() => setShow(false), [setShow]);

  const [shares, setShares] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && idExhibicion) {
      loadData();
    }
  }, [show, idExhibicion]);

  async function loadData() {
    setLoading(true);
    try {
      const [sharesRes, ...usersResults] = await Promise.all([
        doFetchJson("EXHI", `exhibiciones/${idExhibicion}/shares`, { method: "GET" }),
        ...appCodes.map((ac) =>
          doFetchJson("EXHI", `app/${encodeURIComponent(ac)}/users`, { method: "GET" })
        ),
      ]);

      if (sharesRes?.STATE === "SUCCESS") {
        setShares(Array.isArray(sharesRes.DATA) ? sharesRes.DATA : []);
      }

      // Merge all eligible users from all appCodes (deduplicate by idUsuario)
      const userMap = {};
      usersResults.forEach((res) => {
        if (res?.STATE === "SUCCESS" && Array.isArray(res.DATA)) {
          res.DATA.forEach((u) => { userMap[u.idUsuario] = u; });
        }
      });
      setEligibleUsers(Object.values(userMap));
      setSelectedUserId("");
    } catch {
      toast.show({ message: "Error cargando datos de compartición", type: "danger" });
    } finally {
      setLoading(false);
    }
  }

  async function handleShare() {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      const res = await doFetchJson("EXHI", `exhibiciones/${idExhibicion}/share`, {
        method: "POST",
        body: new URLSearchParams({ userId: selectedUserId }).toString(),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res?.STATE === "SUCCESS") {
        toast.show({ message: "Exhibición compartida", type: "success" });
        loadData();
        onChanged && onChanged();
      } else {
        toast.show({ message: res?.MESSAGE || "Error al compartir", type: "danger" });
      }
    } catch {
      toast.show({ message: "Error de conexión", type: "danger" });
    } finally {
      setSaving(false);
    }
  }

  async function handleUnshare(userId) {
    setSaving(true);
    try {
      const res = await doFetchJson("EXHI", `exhibiciones/${idExhibicion}/share/${userId}`, {
        method: "DELETE",
      });
      if (res?.STATE === "SUCCESS") {
        toast.show({ message: "Compartición eliminada", type: "success" });
        loadData();
        onChanged && onChanged();
      } else {
        toast.show({ message: res?.MESSAGE || "Error al eliminar compartición", type: "danger" });
      }
    } catch {
      toast.show({ message: "Error de conexión", type: "danger" });
    } finally {
      setSaving(false);
    }
  }

  // Users already shared (by ID)
  const sharedUserIds = new Set(shares.map((s) => s.userId));
  // Users eligible to share with (not already shared)
  const availableUsers = eligibleUsers.filter((u) => !sharedUserIds.has(u.userId));

  return (
    <MDBModal show={show} onHide={handleClose} tabIndex="-1">
      <MDBModalDialog>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>Compartir exhibición</MDBModalTitle>
            <MDBBtn className="btn-close" color="none" onClick={() => setShow(false)} />
          </MDBModalHeader>

          <MDBModalBody>
            {loading ? (
              <div className="text-center py-3">
                <MDBSpinner />
              </div>
            ) : (
              <>
                {/* Compartir con nuevo usuario */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Compartir con</label>
                  <div className="d-flex gap-2">
                    <select
                      className="form-select form-select-sm"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      disabled={saving || availableUsers.length === 0}
                    >
                      <option value="">
                        {availableUsers.length === 0
                          ? "No hay más usuarios disponibles"
                          : "— Selecciona usuario —"}
                      </option>
                      {availableUsers.map((u) => (
                        <option key={u.userId} value={u.userId}>
                          @{u.apodo}
                        </option>
                      ))}
                    </select>
                    <MDBBtn
                      size="sm"
                      disabled={!selectedUserId || saving}
                      onClick={handleShare}
                    >
                      {saving ? <MDBSpinner size="sm" /> : "Añadir"}
                    </MDBBtn>
                  </div>
                </div>

                {/* Lista de comparticiones actuales */}
                <div>
                  <label className="form-label fw-semibold">
                    Compartida con ({shares.length})
                  </label>
                  {shares.length === 0 ? (
                    <p className="text-muted small">No está compartida con nadie todavía.</p>
                  ) : (
                    <MDBListGroup>
                      {shares.map((s) => (
                        <MDBListGroupItem
                          key={s.userId}
                          className="d-flex justify-content-between align-items-center py-2"
                        >
                          <span>@{s.apodo || `Usuario ${s.userId}`}</span>
                          <MDBBtn
                            size="sm"
                            color="danger"
                            outline
                            disabled={saving}
                            onClick={() => handleUnshare(s.userId)}
                          >
                            Quitar
                          </MDBBtn>
                        </MDBListGroupItem>
                      ))}
                    </MDBListGroup>
                  )}
                </div>
              </>
            )}
          </MDBModalBody>

          <MDBModalFooter>
            <MDBBtn color="secondary" size="sm" onClick={() => setShow(false)}>
              Cerrar
            </MDBBtn>
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}
